import { useMemo } from "react"
import Separator from "@/v2-ui/separator"
import Table from "@/v2-backoffice-client/components/table"
import { TooltipLink } from "@/v2-backoffice-client/components/link"
import LicenseCollectionRowToolbar from "@/v2-backoffice-client/license/collections/LicenseCollectionRowToolbar"
import {
  collectionFilterOptionsOf,
  createCollectionColumns,
  toCollectionRows,
  type CollectionRow,
} from "@/v2-backoffice-client/license/collections/license.collections.columns"
import { useInvoiceCollectionsQuery } from "@/v2-backoffice-client/license/collections/license.collections.query"
import { getCollectionOverdueDays } from "@/v2-backoffice-client/license/collections/license.collections.utils"
import {
  LICENSING_TABS_ID,
  LICENSING_VIEW_COLLECTIONS,
} from "@/v2-backoffice-client/license/license.constants"
import { formatInvoiceAmount } from "@/v2-backoffice-client/customer/billing/customerBilling.utils"

export type CustomerCollectionsProps = {
  customerId: number
}

// The customer panel's slice of the Collections board: every open collection
// item on this customer (past due or in nonpayment, money outstanding), as
// the same table the board is — same columns, badges and row actions, read
// off one shared column set — minus the customer column, since every row is
// this customer's. A debt conversation is startable from the customer
// without hunting the invoice list at the bottom of the tab. Renders nothing
// when the customer has no open items; settled-after-chasing rows stay on
// the board as the campaign record, here they are one muted line. Shares
// the board's query (Apollo dedupes), so saves made here update the board
// and vice versa.
function CustomerCollections(props: CustomerCollectionsProps) {
  const { customerId } = props

  const { data, previousData, loading, error, refetch } =
    useInvoiceCollectionsQuery()
  // previousData keeps rows rendered through post-save evictions.
  const invoices = (data ?? previousData)?.getInvoiceCollections

  const { openRows, settledCount } = useMemo(() => {
    const customerRows = toCollectionRows(
      (invoices ?? []).filter((invoice) => invoice.CustomerId === customerId),
    )
    return {
      openRows: customerRows
        .filter((invoice) => invoice.OutcomeCode == null)
        .sort(
          (a, b) => getCollectionOverdueDays(b) - getCollectionOverdueDays(a),
        ),
      settledCount: customerRows.filter(
        (invoice) => invoice.OutcomeCode != null,
      ).length,
    }
  }, [invoices, customerId])

  const columns = useMemo(
    () =>
      createCollectionColumns({
        ...collectionFilterOptionsOf(openRows),
        hidesDebtor: true,
      }),
    [openRows],
  )

  const totalOutstanding = openRows.reduce(
    (sum, invoice) => sum + (invoice.Balance ?? 0),
    0,
  )
  // Honest label: a summed number must not claim a currency the rows don't
  // share (same rule as the board's outstanding total).
  const currencies = new Set(
    openRows.map((invoice) => invoice.Currency).filter(Boolean),
  )
  const totalCurrency =
    currencies.size === 1 ? [...currencies][0] : currencies.size ? "mixed" : ""

  // No open debt conversation = no section. The invoice list further down
  // still tells the full history.
  if (openRows.length === 0) return null

  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold">Collections</h3>

      <Table
        className="customer-collection-table"
        tableId="customer-collection-table"
        data={openRows}
        columns={columns}
      >
        <Table.Toolbar>
          <span className="whitespace-nowrap text-xs text-muted-foreground">
            {openRows.length} open invoice{openRows.length === 1 ? "" : "s"} ·{" "}
            {formatInvoiceAmount(totalOutstanding, totalCurrency)} outstanding
          </span>
          <div className="flex-1" />
          <TooltipLink
            variant="ghost"
            size="xs"
            iconSrc="expand"
            to={`/licensing?${LICENSING_TABS_ID}-tab=${LICENSING_VIEW_COLLECTIONS}`}
            content="The whole book's collection worklist — this table is just this customer's slice."
          >
            Collections board
          </TooltipLink>
          <Separator orientation="vertical" />
          <Table.ToolbarMenu
            onRefresh={() => void refetch()}
            exportFileName="collections"
          />
        </Table.Toolbar>

        <Table.RowToolbar<CollectionRow> column="InvoiceNumber">
          {(invoice) => (
            <LicenseCollectionRowToolbar
              invoice={invoice}
              // The payment-request draft names EVERYTHING the customer owes
              // — one email covers the debt, not one per invoice.
              customerOpenInvoices={openRows}
            />
          )}
        </Table.RowToolbar>

        <Table.Content isLoading={loading} error={error} />
      </Table>

      {settledCount > 0 && (
        <p className="text-xs text-muted-foreground">
          {settledCount} chased invoice{settledCount === 1 ? "" : "s"} settled —
          the campaign record lives on the Collections board.
        </p>
      )}
    </section>
  )
}

export default CustomerCollections
